'use client';

import React, {
  ChangeEvent,
  CSSProperties,
  FormEvent,
  HTMLProps,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Input } from '@/components/Forms/Input';
import { Button } from '@/components/Button';
import { Service } from '@/types';
import { Autocomplete } from '@/components/Forms/Autocomplete';
import * as Popover from '@radix-ui/react-popover';
import { usePathname, useRouter } from 'next/navigation';
import NotFound from '@/components/Forms/NotFound';

const content =
  'w-[--radix-popper-anchor-width] max-w-[calc(100vw-2rem)] z-[99] text-sm md:text-base mt-2 outline-0 rounded-md bg-[white] shadow-[0px_0px_7px_0px_hsla(206,22%,7%,.25),0_10px_20px_-15px_hsla(206,22%,7%,.2)] will-change-[transform,opacity] data-[state=open]:data-[side=top]:animate-slideDownAndFade data-[state=open]:data-[side=right]:animate-slideLeftAndFade data-[state=open]:data-[side=bottom]:animate-slideUpAndFade data-[state=open]:data-[side=left]:animate-slideRightAndFade';

interface SearchProps extends HTMLProps<HTMLInputElement> {
  scale?: number;
  marginTop?: number;
  isHeader?: boolean;
  widthForm?: number;
  width?: number;
  justifyContent?: string;
}

export function Search({
                         scale,
                         marginTop,
                         isHeader,
                         widthForm,
                         justifyContent,
                       }: SearchProps) {
  const router = useRouter();
  const pathName = usePathname();

  const inputRef = useRef<HTMLInputElement | null>(null);
  const [segmento, setSegmento] = useState<string>('');
  const [services, setServices] = useState<Array<Service> | undefined>();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [notFound, setNotFound] = useState<boolean>(false);
  const [navigator, setNavigator] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const selectedTermRef = useRef<string | null>(null);
  const listboxId = useId();

  const [displayHome, setDisplayHome] = useState<string>(
    isHeader && pathName == '/' ? 'none' : 'flex',
  );

  const handleInputChange = useCallback(
    ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
      selectedTermRef.current = null;
      setNavigator(null);
      setActiveIndex(-1);
      setSegmento(value || '');
    },
    [],
  );

  useEffect(() => {
    if (segmento.length < 3 || segmento === selectedTermRef.current) {
      setServices(undefined);
      setIsOpen(false);
      if (segmento.length < 3) setNotFound(false);
      return;
    }

    const controller = new AbortController();

    const timer = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ segmento });
        const httpResponse = await fetch(`/api/search?${params}`, {
          signal: controller.signal,
        });

        // Falha do servidor não significa "serviço inexistente": não exibe o aviso de não encontrado.
        if (!httpResponse.ok) {
          setServices(undefined);
          setIsOpen(false);
          return;
        }

        const response = await httpResponse.json();

        const list = response?.data?.ArrayServiceSearch;

        if (Array.isArray(list) && list.length > 0) {
          setServices(list);
          setActiveIndex(-1);
          setIsOpen(true);
          setNotFound(false);
        } else {
          setServices([]);
          setIsOpen(false);
          setNotFound(true);
        }
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          console.error('Erro ao buscar serviços', error);
        }
      }
    }, 300);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [segmento]);

  useLayoutEffect(() => {
    setNotFound(false);
    if (isHeader) {
      if (pathName != '/') setDisplayHome('flex');
      else setDisplayHome('none');
    }
  }, [isHeader, pathName]);

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();

    const target =
      navigator ??
      (services && services.length > 0
        ? services[activeIndex >= 0 ? activeIndex : 0]?.Url
        : undefined);

    if (!target) {
      inputRef.current?.focus();
      return;
    }

    setIsOpen(false);
    router.push(`/${target.replace(/^\/+/, '')}`);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      return;
    }

    if (!isOpen || !services || services.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((index) => (index + 1) % services.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((index) => (index <= 0 ? services.length - 1 : index - 1));
    }
  };

  const handlefocus = (e: any): void => {
    e.preventDefault();
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleClick = (navigator: { term: string; Url: string }) => {
    const { term, Url } = navigator;
    selectedTermRef.current = term;
    setSegmento(term);
    setNavigator(Url);
    setActiveIndex(-1);
    setIsOpen(false);
  };

  const headerSearchStyle: CSSProperties = {
    transform: `scale(${scale})`,
    marginTop: `${marginTop}px`,
    display: `${displayHome}`,
    width: `100%`,
  };

  const headerFormStyle: CSSProperties = {
    width: `${widthForm}%`,
    display: `${displayHome}`,
    justifyContent: `${justifyContent}`,
  };

  return (
    <>
      <Popover.Root open={isOpen}>
        <Popover.Trigger />
        <Popover.Anchor asChild>
          <form onSubmit={handleSearch} style={headerFormStyle}>
            <div
              style={headerSearchStyle}
              className={
                'styled-search-component w-95 flex h-[67px] items-center justify-between rounded-full p-2 md:mt-3 md:h-[60px] md:w-[738px]'
              }
            >
              <Input
                className={
                  'border-none w-[100%] pl-2 text-sm placeholder:text-[#818181] md:w-[450px] md:pl-7 md:text-lg'
                }
                autoComplete={'off'}
                ref={inputRef}
                id={'search'}
                type={'text'}
                placeholder={'Qual serviço você precisa?'}
                aria-label={'Qual serviço você precisa?'}
                role={'combobox'}
                aria-expanded={isOpen}
                aria-controls={listboxId}
                aria-autocomplete={'list'}
                aria-activedescendant={
                  isOpen && activeIndex >= 0
                    ? `${listboxId}-option-${activeIndex}`
                    : undefined
                }
                onKeyDown={handleKeyDown}
                onChange={handleInputChange}
                value={segmento}
              />
              <Button
                title='Buscar'
                type={'submit'}
                className={
                  'hover:text-bold responsive-buscar-text h-[44px] w-[115px] min-w-[83px] items-center rounded-full bg-primary text-[white] transition hover:border-2 hover:border-[#264EC7] hover:text-primary'
                }
              >
                <i className={'mdi mdi-magnify'} /> Buscar
              </Button>
            </div>
          </form>
        </Popover.Anchor>

        <Popover.Portal>
          <Popover.Content
            onInteractOutside={() => setIsOpen(false)}
            className={content}
            onOpenAutoFocus={handlefocus}
          >
            {services && (
              <Autocomplete
                callback={handleClick}
                services={services}
                listboxId={listboxId}
                activeIndex={activeIndex}
              />
            )}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      <NotFound notFound={notFound}></NotFound>
    </>
  );
}
